import {
  OpenDataClient,
  PricePoint,
  Stock,
  Token,
} from "../features/api/openData";
import {
  PositionAsset,
  PositionAssetTypeEnum,
  PositionPerformancePoint,
} from "../features/api/memberApi";

import type { FolderInfo } from "../store/favorite/types";
import bigDecimal from "js-big-decimal";
import { dayjs } from "utils";
import { keyBy } from "lodash-es";

type IParams = {
  ids: string[];
  asset_type: PositionAssetTypeEnum;
  client: OpenDataClient<any>;
};
type IStockParams = NonNullable<
  Parameters<OpenDataClient<unknown>["stocks"]["getStocks"]>[0]
>;
type ITokenParams = NonNullable<
  Parameters<OpenDataClient<unknown>["tokens"]["getTokens"]>[0]
>;

export const getAssetInfo = async (
  { ids, asset_type, client }: IParams,
  options?: IStockParams | ITokenParams,
) => {
  if (asset_type === PositionAssetTypeEnum.TOKEN) {
    const result = await client.tokens.getTokens({
      ids,
      limit: ids.length,
      "enrich.with_market.enable": true,
      "enrich.with_sparkline.enable": true,
      "enrich.with_sparkline.interval": "1d",
      "enrich.with_sparkline.trading_period": "5d",
      "enrich.enable": true,
      ...options,
    });
    return result.data.data?.rows ?? [];
  }
  const result = await client.stocks.getStocks({
    ids,
    limit: ids.length,
    "enrich.with_trading_data": true,
    "enrich.enable": true,
    "enrich.with_sparkline.enable": false,
    "enrich.with_sparkline.interval": "1d",
    "enrich.with_sparkline.trading_period": "5d",
    ...options,
  });
  return result.data.data?.rows ?? [];
};

// 校验number有效性（类型守卫）
const isValidNumber = (value?: number | null): value is number => {
  return typeof value === "number" && !isNaN(value) && isFinite(value);
};

// 不是有效number返回0
export const returnZeroIfInvalidNumber = (value?: number | null): number => {
  if (!isValidNumber(value)) {
    return 0;
  }
  return value;
};

/**
 * 根据文件夹列表和资产情况，计算默认的 folder ID
 *
 * @description 该函数实现以下逻辑：
 * - 规则1: 无收藏资产且无连接外部账号 -> 使用 EDGEN id
 * - 规则2: userFolders.slice(1) 中有资产，userFolders[0] 无资产 -> 取第一个有资产的 id
 * - 规则3: userFolders[0] 和 userFolders.slice(1) 都有资产 -> 取外部账户中第一次导入的（根据时间排序）
 * - Fallback: 只有 EDGEN 有资产 -> 使用 EDGEN id
 *
 * @param folders - 文件夹列表，其中 folders[0] 是 EDGEN 文件夹，其余为外部账户文件夹
 * @returns 计算出的默认 folder ID，如果无有效数据则返回空字符串
 *
 * @example
 * ```ts
 * const folders = [
 *   { id: 'edgen-1', asset_count: 0 },
 *   { id: 'external-1', asset_count: 5, created_at: '2024-01-01' },
 *   { id: 'external-2', asset_count: 3, created_at: '2024-01-02' }
 * ];
 * const folderId = getDefaultFolderId(folders); // 返回 'external-1'
 * ```
 */
export const getDefaultFolderId = (
  folders: readonly FolderInfo[] | null | undefined,
): string => {
  if (!folders || folders.length === 0) {
    return "";
  }

  const edgenFolder = folders[0];
  const externalFolders = folders.slice(1);

  const hasEdgenAssets = (edgenFolder?.asset_count ?? 0) > 0;
  const externalFoldersWithAssets = externalFolders.filter(
    (folder) => (folder?.asset_count ?? 0) > 0,
  );
  const hasExternalAssets = externalFoldersWithAssets.length > 0;

  // 规则1: 无收藏资产且无连接外部账号 -> 使用 EDGEN id
  if (!hasEdgenAssets && !hasExternalAssets) {
    return edgenFolder?.id || "";
  }

  // 规则2: userFolders.slice(1) 中有资产，userFolders[0] 无资产 -> 取第一个有资产的 id
  if (!hasEdgenAssets && hasExternalAssets) {
    return externalFoldersWithAssets[0]?.id || "";
  }

  // 规则3: userFolders[0] 和 userFolders.slice(1) 都有资产 -> 取外部账户中第一次导入的
  if (hasEdgenAssets && hasExternalAssets) {
    const sorted = [...externalFoldersWithAssets].sort((a, b) => {
      const aTime = a.updated_at || a.created_at || "";
      const bTime = b.updated_at || b.created_at || "";
      return new Date(aTime).getTime() - new Date(bTime).getTime();
    });
    return sorted[0]?.id || "";
  }

  // Fallback: 只有 EDGEN 有资产
  return edgenFolder?.id || "";
};

// merge 后的数据结构
export type MergedContent = PositionAsset & {
  asset?: Token & Stock;
  value?: number;
  prevValue?: number;
  today_pnl?: number;
  total_pnl?: number;
  today_pnl_percent?: number;
  total_pnl_percent?: number;
  today_un_pnl?: number;
  today_un_pnl_percent?: number;
  cumulative_net_buy_value?: number;
  cumulative_buy_value?: number;
  today_increment_buy_value_total?: number;
  change1d?: number | string;
};

const mergeContents = (
  contents: PositionAsset[],
  assets: Token[] | Stock[],
): MergedContent[] => {
  if (!Array.isArray(contents) || contents.length === 0) {
    return [];
  }
  if (!Array.isArray(assets) || assets.length === 0) {
    return contents;
  }
  // 利用lodash 以 assets.id 为key将其转为object
  const assetsMap = keyBy(assets, "id") as Record<string, Token | Stock>;
  return contents
    .filter((item) => !!assetsMap[item.asset_id!])
    .map((item) => {
      const asset = assetsMap[item.asset_id!];
      const amount = returnZeroIfInvalidNumber(Number(item.amount));
      let price =
        item.asset_type === PositionAssetTypeEnum.TOKEN
          ? (asset as Token)?.market_data?.current_price
          : (asset as Stock)?.trading_data?.current_price;
      price = returnZeroIfInvalidNumber(Number(price));
      const value = Number(
        new bigDecimal(amount).multiply(new bigDecimal(price)).getValue(),
      );
      let prevPrice =
        item.asset_type === PositionAssetTypeEnum.TOKEN
          ? (asset as Token)?.sparkline?.at(-1)?.price
          : (asset as Stock)?.trading_data?.previous_close;
      prevPrice = returnZeroIfInvalidNumber(Number(prevPrice));
      const previous_close_amount = returnZeroIfInvalidNumber(
        Number(item.previous_close_amount),
      );
      const prevValue = Number(
        new bigDecimal(previous_close_amount)
          .multiply(new bigDecimal(prevPrice))
          .getValue(),
      );
      const today_increment_free = returnZeroIfInvalidNumber(
        Number(item.today_increment_fee),
      );
      const today_increment_net_buy_value = returnZeroIfInvalidNumber(
        Number(item.today_increment_net_buy_value),
      );
      const today_increment_buy_value = returnZeroIfInvalidNumber(
        Number(item.today_increment_buy_value),
      );
      const cumulative_net_buy_value = returnZeroIfInvalidNumber(
        Number(item.cumulative_net_buy_value),
      );
      const cumulative_buy_value = returnZeroIfInvalidNumber(
        Number(item.cumulative_buy_value),
      );
      const cumulative_fee = returnZeroIfInvalidNumber(
        Number(item.cumulative_fee),
      );
      const today_pnl = Number(
        new bigDecimal(value)
          .subtract(new bigDecimal(prevValue))
          .subtract(new bigDecimal(today_increment_net_buy_value))
          .subtract(new bigDecimal(today_increment_free))
          .getValue(),
      );

      const today_increment_buy_value_total = Number(
        new bigDecimal(prevValue)
          .add(new bigDecimal(today_increment_buy_value))
          .getValue(),
      );
      const today_pnl_percent =
        today_increment_buy_value_total <= 0
          ? undefined
          : returnZeroIfInvalidNumber(
              Number(
                new bigDecimal(today_pnl)
                  .divide(
                    new bigDecimal(prevValue).add(
                      new bigDecimal(today_increment_buy_value),
                    ),
                  )
                  .multiply(new bigDecimal(100))
                  .getValue(),
              ),
            );
      const total_pnl = Number(
        new bigDecimal(value)
          .subtract(new bigDecimal(cumulative_net_buy_value))
          .subtract(new bigDecimal(cumulative_fee))
          .getValue(),
      );
      const total_pnl_percent =
        cumulative_buy_value <= 0
          ? undefined
          : returnZeroIfInvalidNumber(
              Number(
                new bigDecimal(total_pnl)
                  .divide(new bigDecimal(cumulative_buy_value))
                  .multiply(new bigDecimal(100))
                  .getValue(),
              ),
            );
      let change1d =
        item.asset_type === PositionAssetTypeEnum.TOKEN
          ? (asset as Token).market_data?.price_change_percentage?.find(
              (item) => item.time_period === "24h",
            )?.percentage
          : "";
      if (item.asset_type === PositionAssetTypeEnum.STOCK) {
        change1d = !prevPrice
          ? undefined
          : returnZeroIfInvalidNumber(
              Number(
                new bigDecimal(price)
                  .subtract(new bigDecimal(prevPrice))
                  .divide(new bigDecimal(prevPrice))
                  .multiply(new bigDecimal(100))
                  .getValue(),
              ),
            );
      }
      const today_un_pnl = Number(
        new bigDecimal(amount)
          .multiply(new bigDecimal(price).subtract(new bigDecimal(prevPrice)))
          .getValue(),
      );
      const today_un_pnl_percent =
        prevValue <= 0
          ? undefined
          : Number(
              new bigDecimal(today_un_pnl)
                .divide(new bigDecimal(prevValue))
                .multiply(new bigDecimal(100))
                .getValue(),
            );
      return {
        ...item,
        asset: assetsMap[item.asset_id!],
        today_pnl,
        total_pnl_percent,
        total_pnl,
        today_pnl_percent,
        today_increment_buy_value,
        today_increment_buy_value_total,
        today_un_pnl,
        today_un_pnl_percent,
        cumulative_net_buy_value,
        cumulative_buy_value,
        today_increment_net_buy_value,
        cumulative_fee,
        prevValue,
        value,
        change1d,
      };
    });
};

export function mapSummaryRowsToMergedContents(
  rows: AssetGroupDTO[],
  tokenAssets: Token[],
  stockAssets: Stock[],
): { tokens: MergedContent[]; stocks: MergedContent[] } {
  const positionAssets = summaryRowsToPositionAssets(rows);
  const tokenRows = positionAssets.filter(
    (item) => item.asset_type === PositionAssetTypeEnum.TOKEN,
  );
  const stockRows = positionAssets.filter(
    (item) => item.asset_type === PositionAssetTypeEnum.STOCK,
  );

  return {
    tokens: mergeContents(tokenRows, tokenAssets),
    stocks: mergeContents(stockRows, stockAssets),
  };
}

type OwnerPositionSummaryApiClient = Pick<MemberClient<unknown>, "positions">;

/** Fetch invest portfolio holdings via owner summary + openData enrich. */
export async function fetchInvestPortfolioAssetsFromSummary(
  portfolioId: string,
  memberApi: OwnerPositionSummaryApiClient,
  openDataClient: OpenDataClient<any>,
): Promise<{ tokens: MergedContent[]; stocks: MergedContent[] }> {
  const accountIds =
    portfolioId === ALL_PORTFOLIO_OPTION_ID ? undefined : [portfolioId];
  const rows = await fetchOwnerPositionSummaryAllPages(memberApi, {
    accountIds,
  });
  const { tokenIds, stockIds } = splitSummaryRowAssetIds(rows);

  const [tokenAssets, stockAssets] = await Promise.all([
    tokenIds.length > 0
      ? getAssetInfo({
          ids: tokenIds,
          asset_type: PositionAssetTypeEnum.TOKEN,
          client: openDataClient,
        })
      : Promise.resolve([]),
    stockIds.length > 0
      ? getAssetInfo({
          ids: stockIds,
          asset_type: PositionAssetTypeEnum.STOCK,
          client: openDataClient,
        })
      : Promise.resolve([]),
  ]);

  return mapSummaryRowsToMergedContents(rows, tokenAssets, stockAssets);
}

import type { AssetGroupDTO, MemberClient } from "../features/api/memberApi";
import { ALL_PORTFOLIO_OPTION_ID } from "../components/PortfolioContainer/constants";
import {
  fetchOwnerPositionSummaryAllPages,
  splitSummaryRowAssetIds,
  summaryRowsToPositionAssets,
} from "./portfolioOwnerSummary";

type IGetPerformanceParams = {
  pnl?: PositionPerformancePoint[];
  gspc?: Token[] | Stock[];
  btc?: Token[] | Stock[];
};

const extractSparkline = (assets?: (Token | Stock)[]): PricePoint[] => {
  if (!Array.isArray(assets) || assets.length === 0) {
    return [];
  }
  const lines =
    assets?.[0]?.sparkline?.sort(
      (a, b) =>
        returnZeroIfInvalidNumber(a.timestamp) -
        returnZeroIfInvalidNumber(b.timestamp),
    ) ?? [];
  const sparkline = lines.map((item) => ({
    ...item,
    label: dayjs(item.timestamp).format("MM/DD HH:mm"),
  }));
  return sparkline;
};

type PerformanceData = { value: number; percentage: number };

export type IAlignPnlGspcBtcDataResult = {
  XAxis: { x: number; label: string }[];
  YPnl: number[];
  YGspc?: number[];
  YBtc?: number[];
  dataValue: Array<{
    timestamp: number;
    pnl: PerformanceData;
    gspc?: PerformanceData;
    btc?: PerformanceData;
  }>;
};

// 生成performance chart data
export const genPerformance = ({
  pnl,
  gspc,
  btc,
}: IGetPerformanceParams): MaybeNullable<IAlignPnlGspcBtcDataResult> => {
  if (!Array.isArray(pnl) || pnl.length === 0) {
    return undefined;
  }
  const gspcSparkline = extractSparkline(gspc);
  const btcSparkline = extractSparkline(btc);
  // 若两者均无 sparkline，直接返回 pnl
  if (gspcSparkline.length === 0 && btcSparkline.length === 0) {
    return pnl.reduce<IAlignPnlGspcBtcDataResult>(
      (acc, curr, index) => {
        acc.XAxis.push({
          x: index,
          label: dayjs(curr.time).format("MMM DD, HH:mm"),
        });
        acc.YPnl.push(curr.total_cumulative_pnl_percent ?? 0);
        acc.dataValue.push({
          timestamp: curr.time ?? 0,
          pnl: {
            value: curr.total_cumulative_pnl!,
            percentage: curr.total_cumulative_pnl_percent!,
          },
        });
        return acc;
      },
      { XAxis: [], YPnl: [], dataValue: [] },
    );
  }

  let gspcIndex = 0;
  let btcIndex = 0;
  let lastGspcPrice: number;
  let lastBtcPrice: number;
  let btcStartPrice: number | undefined;
  let gspcStartPrice: number | undefined;
  const XAxis: { x: number; label: string }[] = [];
  const YPnl: number[] = [];
  const YGspc: number[] = [];
  const YBtc: number[] = [];
  const dataValue: IAlignPnlGspcBtcDataResult["dataValue"] = [];
  pnl.forEach((pnlPoint, index) => {
    const currentTime = pnlPoint.time ?? 0;
    let gspcItem = gspcSparkline[gspcIndex];
    let gspcTimestamp = returnZeroIfInvalidNumber(gspcItem?.timestamp);
    // Advance GSPC pointer
    while (gspcIndex < gspcSparkline.length && gspcTimestamp <= currentTime) {
      lastGspcPrice = returnZeroIfInvalidNumber(Number(gspcItem?.price));
      gspcIndex++;
      gspcItem = gspcSparkline[gspcIndex];
      gspcTimestamp = returnZeroIfInvalidNumber(gspcItem?.timestamp);
    }
    btcStartPrice =
      typeof btcStartPrice === "undefined" ? lastBtcPrice : btcStartPrice;
    let btcItem = btcSparkline[btcIndex];
    let btcTimestamp = returnZeroIfInvalidNumber(btcItem?.timestamp);
    // Advance BTC pointer
    while (btcIndex < btcSparkline.length && btcTimestamp <= currentTime) {
      lastBtcPrice = returnZeroIfInvalidNumber(Number(btcItem?.price));
      btcIndex++;
      btcItem = btcSparkline[btcIndex];
      btcTimestamp = returnZeroIfInvalidNumber(btcItem?.timestamp);
    }
    gspcStartPrice =
      typeof gspcStartPrice === "undefined" ? lastGspcPrice : gspcStartPrice;
    const XLabel = dayjs(currentTime).format("MMM DD, HH:mm");
    const total_cumulative_pnl_percent =
      returnZeroIfInvalidNumber(Number(pnlPoint.total_cumulative_pnl_percent)) *
      100;
    XAxis.push({ x: index, label: XLabel });
    YPnl.push(total_cumulative_pnl_percent);
    const btcPercentage = returnZeroIfInvalidNumber(
      ((lastBtcPrice - btcStartPrice) / btcStartPrice) * 100,
    );
    const gspcPercentage = returnZeroIfInvalidNumber(
      ((lastGspcPrice - gspcStartPrice) / gspcStartPrice) * 100,
    );
    YBtc.push(btcPercentage);
    YGspc.push(gspcPercentage);
    dataValue.push({
      timestamp: currentTime ?? 0,
      pnl: {
        value: Number(pnlPoint.total_cumulative_pnl!),
        percentage: total_cumulative_pnl_percent,
      },
      gspc: { value: lastGspcPrice, percentage: gspcPercentage },
      btc: { value: lastBtcPrice, percentage: btcPercentage },
    });
  });

  return {
    XAxis,
    YPnl,
    YGspc,
    YBtc,
    dataValue,
  };
};
